/**
 * QuickBooks transaction → deal link overrides.
 *
 * The QB sync auto-matches each Payment / QBExpense to a deal based on
 * customer name. PMs can override that link from the Project P&L view
 * when the auto-matcher gets it wrong (e.g. customer renamed in QB,
 * legacy invoice billed to the wrong client, kit refreshed under a new
 * project, etc.).
 *
 * The override is written directly onto the QB transaction doc as
 * `matchedDealId` / `matchedDealTitle`, plus a `matchedDealIdOverridden`
 * flag so the auto-matcher (cloud function / scheduled job) knows to
 * leave it alone on subsequent runs.
 */

import { deleteField, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { TransactionSplit } from '../types';

export interface DealLinkContext {
  uid?: string;
  /** Optional human-friendly title to store alongside the id (so historical reads don't need a deal join). */
  dealTitle?: string;
}

/**
 * Override the deal a QB Payment is linked to.
 *
 * Pass `null` for `dealId` to clear the link entirely (treats the payment
 * as unmatched / uncategorized).
 */
export async function setPaymentDealLink(
  paymentId: string,
  dealId: string | null,
  ctx: DealLinkContext = {},
): Promise<void> {
  if (!paymentId) return;
  const ref = doc(db, 'payments', paymentId);
  const payload: Record<string, any> = {
    matchedDealIdOverridden: true,
    updatedAt: serverTimestamp(),
  };
  if (ctx.uid) payload.matchedDealOverrideBy = ctx.uid;
  if (dealId) {
    payload.matchedDealId = dealId;
    if (ctx.dealTitle) payload.matchedDealTitle = ctx.dealTitle;
  } else {
    payload.matchedDealId = deleteField();
    payload.matchedDealTitle = deleteField();
  }
  await updateDoc(ref, payload);
}

/**
 * Override the deal a QB Expense is linked to. Also re-categorizes the
 * expense as `direct` (when assigned) or `unmatched` (when cleared) so
 * it slots into the right pool in `aggregateProjectPLBatch`.
 */
export async function setExpenseDealLink(
  expenseId: string,
  dealId: string | null,
  ctx: DealLinkContext = {},
): Promise<void> {
  if (!expenseId) return;
  const ref = doc(db, 'qbExpenses', expenseId);
  const payload: Record<string, any> = {
    matchedDealIdOverridden: true,
    updatedAt: serverTimestamp(),
  };
  if (ctx.uid) payload.matchedDealOverrideBy = ctx.uid;
  if (dealId) {
    payload.matchedDealId = dealId;
    payload.category = 'direct';
    payload.categorizationStatus = 'categorized';
    payload.categorizedAt = serverTimestamp();
    if (ctx.uid) payload.categorizedBy = ctx.uid;
    if (ctx.dealTitle) payload.matchedDealTitle = ctx.dealTitle;
  } else {
    payload.matchedDealId = deleteField();
    payload.matchedDealTitle = deleteField();
    payload.category = 'unmatched';
  }
  await updateDoc(ref, payload);
}

/** Sentinel value for expense project pickers — company-wide overhead pool. */
export const QB_EXPENSE_OVERHEAD_ASSIGN = '__overhead__';

/**
 * Move a QB expense into the shared overhead pool and detach it from any project.
 */
export async function setExpenseAsOverhead(
  expenseId: string,
  ctx: DealLinkContext = {},
): Promise<void> {
  if (!expenseId) return;
  const ref = doc(db, 'qbExpenses', expenseId);
  const payload: Record<string, any> = {
    category: 'overhead',
    categoryOverridden: true,
    matchedDealIdOverridden: true,
    categorizationStatus: 'categorized',
    categorizedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (ctx.uid) {
    payload.categoryOverrideBy = ctx.uid;
    payload.matchedDealOverrideBy = ctx.uid;
    payload.categorizedBy = ctx.uid;
  }
  payload.matchedDealId = deleteField();
  payload.matchedDealTitle = deleteField();
  await updateDoc(ref, payload);
}

/** Re-categorize a QB expense as overhead (or back to direct/unmatched). */
export async function setExpenseCategoryOverride(
  expenseId: string,
  category: 'direct' | 'overhead' | 'unmatched',
  ctx: DealLinkContext = {},
): Promise<void> {
  if (!expenseId) return;
  const ref = doc(db, 'qbExpenses', expenseId);
  const payload: Record<string, any> = {
    category,
    categoryOverridden: true,
    updatedAt: serverTimestamp(),
  };
  if (ctx.uid) payload.categoryOverrideBy = ctx.uid;
  await updateDoc(ref, payload);
}

/**
 * Persist a split allocation on a QB transaction. Pass `null` (or empty array)
 * to clear an existing split and revert to the single-deal `matchedDealId`.
 *
 * Slice amounts do not need to sum to the transaction total — unattributed
 * remainder is intentional when part of the payment belongs to jobs not modeled
 * in this CRM.
 *
 * Cleans the input to ensure each slice has a sane positive amount, then
 * writes atomically. The aggregator at `aggregateProjectPL` honors splits
 * automatically; no other migration is needed.
 */
export async function setTransactionSplits(
  kind: 'payment' | 'expense',
  txnId: string,
  splits: TransactionSplit[] | null,
  ctx: { uid?: string } = {},
): Promise<void> {
  if (!txnId) return;
  const collection = kind === 'payment' ? 'payments' : 'qbExpenses';
  const ref = doc(db, collection, txnId);

  const payload: Record<string, any> = {
    updatedAt: serverTimestamp(),
  };

  if (!splits || splits.length === 0) {
    payload.splits = deleteField();
    payload.splitsAt = deleteField();
    payload.splitsBy = deleteField();
  } else {
    const cleaned = splits
      .filter(s => Number.isFinite(s.amount) && s.amount > 0)
      .map(s => ({
        dealId: s.dealId || null,
        ...(s.projectPhase ? { projectPhase: s.projectPhase } : {}),
        ...(s.dealTitle ? { dealTitle: s.dealTitle } : {}),
        amount: Number(s.amount.toFixed(2)),
        ...(s.note ? { note: s.note } : {}),
      }));
    if (cleaned.length === 0) {
      payload.splits = deleteField();
      payload.splitsAt = deleteField();
      payload.splitsBy = deleteField();
    } else {
      payload.splits = cleaned;
      payload.splitsAt = serverTimestamp();
      if (ctx.uid) payload.splitsBy = ctx.uid;
      payload.matchedDealIdOverridden = true;
      if (kind === 'expense') {
        payload.category = 'direct';
        payload.categorizationStatus = 'categorized';
        payload.categorizedAt = serverTimestamp();
        if (ctx.uid) payload.categorizedBy = ctx.uid;
        payload.matchedDealId = deleteField();
        payload.matchedDealTitle = deleteField();
      } else {
        payload.matchedDealId = deleteField();
        payload.matchedDealTitle = deleteField();
      }
    }
  }

  await updateDoc(ref, payload);
}
